import { css } from "@compiled/react";
import { defineMessage, FormattedMessage } from "react-intl";

import { N700 } from "@atlaskit/theme/colors";
import { token } from "@atlaskit/tokens";

import { AccountCreation } from "@/components/signup/account-creation/account-creation";
import { checkFeatureFlagGate } from "@/utils/feature-flags/check-feature-flag";
import { featureFlags } from "@/utils/feature-flags/feature-flag-manifest";

import { EmbeddedSignupFormWrapper } from "./components/embedded-signup-form-wrapper";
import { ExistingProductText } from "./components/existing-product-text";

const confluenceEmbeddedExistingText = defineMessage({
    id: "BxpUiSignup.SignupForm.existingProduct.confluence.embedded",
    defaultMessage: "Trying to access {product}? <logInLink>Log in</logInLink>"
});

type ConfluenceFeaturesPageEmbeddedSignupFormProps = {
    variant: ConfAnchorVariant;
};

type ConfAnchorVariant =
    | "paid-lp-conf-features"
    | "anchor-lp-conf-competitors"
    | "anchor-lp-conf-work-management"
    | "anchor-lp-conf-teamwork"
    | "anchor-lp-conf-collaboration"
    | "anchor-lp-conf-automation"
    | "paid-lp-confl-template"
    | "paid-lp-confl-documentation"
    | "paid-lp-confl-projectmgmt";

export const ConfluenceFeaturesPageEmbeddedSignupForm = ({
    variant
}: ConfluenceFeaturesPageEmbeddedSignupFormProps) => {
    const gwp9286ConfluenceAnchorUpdates = checkFeatureFlagGate(
        featureFlags.gwp9286ConfluenceAnchorUpdates
    );

    const headers = {
        "paid-lp-conf-features": (
            <FormattedMessage
                id="BxpUiSignup.EmbeddedSignupForm1466.header.confluence.features"
                defaultMessage="Goodbye silos, hello teamwork with Confluence"
            />
        ),
        "anchor-lp-conf-competitors": (
            <FormattedMessage
                id="BxpUiSignup.EmbeddedSignupForm1466.header.confluence.competitors"
                defaultMessage="Confluence is the #1 collaboration tool for productive teams"
            />
        ),
        "anchor-lp-conf-work-management": (
            <FormattedMessage
                id="BxpUiSignup.EmbeddedSignupForm1466.header.confluence.work.management"
                defaultMessage="Bring your team’s work together with Confluence"
            />
        ),
        "anchor-lp-conf-teamwork": (
            <FormattedMessage
                id="BxpUiSignup.EmbeddedSignupForm1466.header.confluence.teamwork"
                defaultMessage="Accelerate teamwork and productivity with Confluence"
            />
        ),
        "anchor-lp-conf-collaboration": gwp9286ConfluenceAnchorUpdates ? (
            <FormattedMessage
                id="GWP9286ConfluenceAnchorUpdates.header.confluence.collaboration"
                defaultMessage="AI-driven project collaboration for modern teams"
            />
        ) : (
            <FormattedMessage
                id="BxpUiSignup.EmbeddedSignupForm1466.header.confluence.collaboration"
                defaultMessage="Turn ideas into action with Confluence"
            />
        ),
        "anchor-lp-conf-automation": (
            <FormattedMessage
                id="BxpUiSignup.EmbeddedSignupForm1466.header.confluence.automation"
                defaultMessage="Collaborate better with Confluence"
            />
        ),
        "paid-lp-confl-template": (
            <FormattedMessage
                id="BxpUiSignup.EmbeddedSignupForm1466.header.confluence.template"
                defaultMessage="There’s a Confluence template for that"
            />
        ),
        "paid-lp-confl-documentation": gwp9286ConfluenceAnchorUpdates ? (
            <FormattedMessage
                id="GWP9286ConfluenceAnchorUpdates.header.confluence.documentation"
                defaultMessage="Turn team knowledge into lasting documentation everyone can use"
            />
        ) : (
            <FormattedMessage
                id="BxpUiSignup.EmbeddedSignupForm1466.header.confluence.documentation"
                defaultMessage="Technically superior documentation starts with Confluence"
            />
        ),
        "paid-lp-confl-projectmgmt": (
            <FormattedMessage
                id="BxpUiSignup.EmbeddedSignupForm1466.header.confluence.project.management"
                defaultMessage="Confluence makes project management easy"
            />
        )
    };

    return (
        <EmbeddedSignupFormWrapper hideFooter={true}>
            <div css={containerStyle} data-testid="signup-form">
                <h1 css={headerStyle}>{headers[variant]}</h1>
                <h2 css={subheaderStyle}>
                    <FormattedMessage
                        id="BxpUiSignup.SignupForm.subheader.1"
                        defaultMessage="It's free for up to 10 users — no credit card needed."
                    />
                </h2>
                <AccountCreation Login={null} />
                <ExistingProductText
                    messageOverride={confluenceEmbeddedExistingText}
                />
            </div>
        </EmbeddedSignupFormWrapper>
    );
};

const containerStyle = css({
    alignItems: "stretch",
    backgroundColor: "white",
    boxShadow: token("elevation.shadow.overlay"),
    borderRadius: "20px",
    color: N700,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    padding: "32px 50px",
    width: "468px"
});

const headerStyle = css({
    paddingInlineEnd: "64px",
    textAlign: "left !important"
});

const subheaderStyle = css({
    fontSize: "16px !important",
    fontWeight: "300 !important",
    textAlign: "left !important"
});
